/**
 * @bos/portal3 — the QUESTION ROUND: what the machine shows when it has stalled with
 * something to ask. Fed by `runs.questions` (question text does not ride the frames),
 * answered through `runs.answer`.
 *
 * WHERE THE CARDS LIVE NOW. A question already carries the stage it came from, so it can be
 * rendered in the room that holds that stage's artifact — beside the rule it is about, the
 * permission it is about, the model it is about. That is what `questionRouting.ts` computes
 * and what {@link RoomQuestions} renders. What is left HERE, on the Overview, is:
 *
 *   - the ROUND SUMMARY — "The machine stopped to ask — 3 questions: 2 about Rules, 1 about
 *     Permissions", each fragment a door into the room that holds them;
 *   - every question NO ROOM CAN HOLD, in full, answerable exactly as before. This is the
 *     guarantee that outranks the feature: a question is never routed somewhere it cannot be
 *     answered, so an unknown stage (a newer chain's, or none at all) lands here;
 *   - the round-level acts (resume / continue) — they belong to the run, not to a room;
 *   - the banked answers whose questions have drifted away, which carry no stage and so have
 *     no room to go to.
 *
 * HONESTY RULES (unchanged, all of them):
 *   - the card renders `ownerHeadline` / `ownerSubject` / `ownerValue` DIRECTLY — the lines
 *     the server wrote — and invents no headline the server didn't say (batch 3 hazard #3).
 *     The headline is per CODE and titles the card; the subject is per INSTANCE and stays
 *     beneath it, because promoting one IN PLACE OF the other would make five advisories of
 *     one code into five identical cards again (the D58 defect);
 *   - `answerKind: 'none'` gets NO input box: the build stopped on a defect nothing an owner
 *     could type would reach, and the card says exactly that;
 *   - an `approve` question offers the two answers the door actually accepts;
 *   - advisories ("settled for you") are SHOWN AND CORRECTABLE (B1, run006).
 *
 * NEW HERE, from the owner-facing review (M4 + M6):
 *   - M4 — a message that welds an owner sentence to `Technical detail (for your engineer):`
 *     is SPLIT on that literal marker: the owner half is the card, the engineer half folds
 *     away behind "For your engineer". Measured live: four advisories whose good sentence was
 *     buried under ten lines of `could not BIND to a read door`.
 *   - M6 — byte-identical rows fold into one with an ×N count. Measured live: five of six
 *     banked answers were word-for-word the same sentence.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  AnswerRunResult,
  CheckpointAnswer,
  CheckpointQuestion,
  RunQuestionsResult,
} from '@bos/portal3-server';
import { PATHS } from '../routes';
import { runErrorMessage } from './hooks';
import {
  countIdentical,
  countPhrase,
  roundFragments,
  routeQuestions,
  splitEngineerDetail,
} from './questionRouting';

/** The reserved non-affirmative answer for an approve question (server contract). */
const HOLD = 'hold';

/** Mirror of `@bos/checkpoint-shared`'s `heldDecisionHasConsumer` (server contract — blind-audit
 *  finding 7): the stages whose recorded correction actually reaches the next derivation (the ER
 *  channel, the six guidance-consuming architect stages, judgment via grading). A correction on any
 *  other stage is recorded and kept visible as a standing work item — promising re-derivation for it
 *  was the unbacked sentence the audit flagged. */
const CONSUMED_STAGES: ReadonlySet<string> = new Set([
  'er',
  'statemachine',
  'process',
  'rules',
  'rbac',
  'audit',
  'decomposition',
  'judgment',
]);

/** How a card renders the strip above it, when the caller has one to render. Injected rather
 *  than imported so this file stays free of the intake payload's shape. */
export type StripFor = (q: CheckpointQuestion) => ReactNode;

function severityChip(q: CheckpointQuestion): { label: string; cls: string } {
  switch (q.severity) {
    case 'block':
      return { label: 'Blocking', cls: 'qchip--block' };
    case 'should':
      return { label: 'Wants your OK', cls: 'qchip--should' };
    default:
      return { label: 'Heads-up', cls: 'qchip--warn' };
  }
}

/** The one sentence rendered for the answer outcome — the server's reason, in owner words. */
export function answerResultSentence(result: AnswerRunResult): string {
  if (result.autoResumed) return 'Answers sent — the machine is continuing.';
  if (result.resumed && result.reason === 'blocking-rederive') {
    return 'Answers sent — the machine is re-deriving with your guidance.';
  }
  switch (result.reason) {
    case 'guidance-unchanged':
      return 'Your guidance is unchanged from the last try, so running again would stop at the same place. Change an answer before resuming.';
    case 'blocking-findings':
      return 'Recorded. Blocking items remain — once the whole round is answered, a resume re-derives with your guidance.';
    case 'assumption-recorded':
      // Finding 2 wording: "the next run" read as ANY next run — false (a new run starts from a
      // fresh directory). The correction reaches THIS build's next derivation, on its next resume.
      return 'Correction recorded — this build re-derives with it when it next runs.';
    default:
      return result.complete
        ? 'All answers recorded.'
        : `Recorded — ${result.answeredCount} of ${result.total} answered.`;
  }
}

/**
 * M4 — the half of the message that was written for whoever writes code, folded away.
 *
 * It is NOT deleted and NOT rewritten: it is the machine's own sentence, one click from the
 * owner's, where an engineer they forward this to will find it immediately. What it stops doing
 * is drowning the sentence the owner was supposed to read.
 */
function EngineerDetail({ text }: { text: string | null }) {
  if (text === null) return null;
  return (
    <details className="qcard__eng">
      <summary>For your engineer</summary>
      <p className="qcard__eng-text">{text}</p>
    </details>
  );
}

function QuestionInput({
  q,
  value,
  onChange,
  disabled,
  noteValue = '',
  onNoteChange,
}: {
  q: CheckpointQuestion;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  noteValue?: string;
  onNoteChange?: (v: string) => void;
}) {
  if (q.answerKind === 'none') {
    return (
      <p className="qcard__none">
        This one stopped the build on a defect that can’t be answered from here — it needs an
        engineer’s fix. There’s nothing to type that would reach anything.
      </p>
    );
  }
  if (q.answerKind === 'approve') {
    return (
      <div className="qcard__choices" role="radiogroup" aria-label={`Answer: ${q.id}`}>
        <label className="qcard__choice">
          <input
            type="radio"
            name={`answer-${q.id}`}
            checked={value === 'approve'}
            onChange={() => onChange('approve')}
            disabled={disabled}
          />
          <span>Approve — continue with this</span>
        </label>
        <label className="qcard__choice">
          <input
            type="radio"
            name={`answer-${q.id}`}
            checked={value === HOLD}
            onChange={() => onChange(HOLD)}
            disabled={disabled}
          />
          <span>Hold — I don’t approve; keep it stopped</span>
        </label>
      </div>
    );
  }
  if (q.answerKind === 'choice') {
    // B16 (run006): the listed options are everything the machine can apply MECHANICALLY — but they
    // are not always everything the owner wants (four gating questions offered only "Drop the
    // filter", and the only clickable path destroyed paid-for brief content). The reserved HOLD
    // answer is the refuse-them-all exit the door accepts: the round keeps holding, and the note
    // travels as re-derivation guidance.
    return (
      <div className="qcard__choices" role="radiogroup" aria-label={`Answer: ${q.id}`}>
        {(q.choices ?? []).map((choice) => (
          <label key={choice} className="qcard__choice">
            <input
              type="radio"
              name={`answer-${q.id}`}
              checked={value === choice}
              onChange={() => onChange(choice)}
              disabled={disabled}
            />
            <span>{choice}</span>
          </label>
        ))}
        <label className="qcard__choice">
          <input
            type="radio"
            name={`answer-${q.id}`}
            checked={value === HOLD}
            onChange={() => onChange(HOLD)}
            disabled={disabled}
          />
          <span>None of these fits — hold it and let me say what I want</span>
        </label>
        {value === HOLD && onNoteChange !== undefined && (
          <textarea
            className="form__textarea qcard__text"
            aria-label={`Note: ${q.id}`}
            placeholder="Say what you want instead — your words guide the next derivation."
            value={noteValue}
            onChange={(e) => onNoteChange(e.target.value)}
            disabled={disabled}
            rows={2}
          />
        )}
      </div>
    );
  }
  // free-text
  return (
    <textarea
      className="form__textarea qcard__text"
      aria-label={`Answer: ${q.id}`}
      placeholder="Tell the machine what’s right here."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={3}
    />
  );
}

/**
 * The "Settled for you" block — every assumption the machine made on the owner's behalf,
 * with a per-advisory correction door (B1). Exported: the run page renders it STANDING
 * (outside any stall) because an auto-approved round never holds the run, so waiting for
 * a stall to show it means never showing it — the measured RUN-006 defect.
 *
 * THE DOOR, PER KIND (answer-pipeline audit finding 3 — the first cut opened it only for
 * `approve` on the false belief no other kind exists; the sign-off round's advisories are
 * all FREE-TEXT, 8 live in RUN-006's round, rendered read-only under copy inviting
 * correction):
 *   - `approve` / `choice` → `{ answer: 'hold', note: <the owner's words> }` (the door's
 *     admitted non-affirmative shape; a choice-kind 'hold' is legal since B16);
 *   - `free-text`          → the owner's words ARE the answer;
 *   - `none`               → read-only (nothing typed can reach anything).
 * Non-affirmative ⇒ substantive ⇒ the decisions channel carries it into the next
 * derivation of THIS build.
 *
 * CLOSED RUNS (finding 2): a 'completed' run can never resume, and a new run starts from
 * a fresh directory — a correction "recorded" there reaches nothing, and the old copy
 * promised the opposite. `runClosed` renders everything read-only under an honest
 * sentence (the server refuses the write too, so the honesty is two-layered).
 *
 * M6, HERE: byte-identical advisories fold into ONE row with an ×N count — and a correction
 * on that row is sent for EVERY id it stands for, because they are one decision written N
 * times. Merging the display while correcting only the first would be a silent partial.
 */
export function AdvisoriesBlock({
  advisories,
  answers,
  onSubmit,
  open = false,
  intro,
  runClosed = false,
  heading,
  stripFor,
}: {
  advisories: readonly CheckpointQuestion[];
  answers: readonly CheckpointAnswer[];
  onSubmit: (answers: CheckpointAnswer[]) => Promise<AnswerRunResult>;
  open?: boolean;
  intro?: string;
  runClosed?: boolean;
  /** The summary line. Defaults to the standing "Settled for you (n)" wording. */
  heading?: string;
  stripFor?: StripFor;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sentence, setSentence] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (advisories.length === 0) return null;

  // M6: identical rows are one row. The key is the WHOLE content (subject, value, message) —
  // never the owner half alone, because two advisories can share an owner sentence and differ
  // entirely in what they are about, and folding those would hide a real finding.
  const rows = countIdentical(
    advisories,
    (a) => `${a.ownerSubject ?? ''} ${a.ownerValue ?? ''} ${a.message} ${a.answerKind}`,
  );
  /** Every id one displayed row stands for — a correction is written against all of them. */
  const idsOf = (key: string): string[] =>
    advisories
      .filter(
        (a) =>
          `${a.ownerSubject ?? ''} ${a.ownerValue ?? ''} ${a.message} ${a.answerKind}` === key,
      )
      .map((a) => a.id);

  /** The recorded correction for an advisory, or null when none was sent: a 'hold' answer's note
   *  (approve/choice kinds), or a free-text advisory's own recorded answer. */
  const recordedCorrection = (q: CheckpointQuestion, ids: readonly string[]): string | null => {
    const a = answers.find((x) => ids.includes(x.questionId));
    if (a === undefined) return null;
    if (a.answer.trim().toLowerCase() === HOLD) return a.note ?? '';
    if (q.answerKind === 'free-text') return a.answer;
    return null;
  };

  /** Which kinds have a correction door at all — 'none' has nothing typed that could reach anything. */
  const correctable = (q: CheckpointQuestion): boolean => q.answerKind !== 'none';

  async function sendCorrection(q: CheckpointQuestion, ids: readonly string[]) {
    const words = (drafts[q.id] ?? '').trim();
    if (words === '') return;
    setBusyId(q.id);
    setError(null);
    setSentence(null);
    try {
      // Finding 3: the door speaks each kind's admitted shape — free-text takes the words AS the
      // answer; approve/choice take the reserved hold with the words riding as the note.
      const batch: CheckpointAnswer[] = ids.map((id) =>
        q.answerKind === 'free-text'
          ? { questionId: id, answer: words }
          : { questionId: id, answer: HOLD, note: words },
      );
      const result = await onSubmit(batch);
      setSentence(answerResultSentence(result));
      setOpenIds((o) => ({ ...o, [q.id]: false }));
    } catch (err) {
      setError(runErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <details className="qround__advisories" open={open}>
      <summary>
        {heading ?? `Settled for you (${advisories.length}) — read what the machine assumed`}
      </summary>
      {runClosed ? (
        <p className="qround__advisory-note">
          This build is finished, so these can no longer be corrected here — a new build starts
          from your description and documents, not from this list. What was assumed stays on
          record below.
        </p>
      ) : (
        intro !== undefined && <p className="qround__advisory-note">{intro}</p>
      )}
      <ul className="qround__advisory-list">
        {rows.map(({ item: a, count, key }) => {
          const ids = idsOf(key);
          const recorded = recordedCorrection(a, ids);
          const split = splitEngineerDetail(a.message);
          // ── THE TITLE OF AN ADVISORY ────────────────────────────────────────────────
          // `ownerHeadline` is the plain-language line the renderer wrote for this finding's
          // CODE — the one a vet-run owner said was the only legible part of a card. It was
          // being deleted at the schema before it ever reached here; now that it survives the
          // read, it is the title, because "A rule needs the app to look up records owned by
          // another part" is what a person can act on and `'Folio'.status must be in [Closed]`
          // is not.
          //
          // AND `ownerSubject` STAYS ON SCREEN BENEATH IT. The headline is per CODE and the
          // subject is per INSTANCE: five of Hotel Lovor's advisories share one headline and
          // differ only in their subject, so promoting the headline OVER the subject rather
          // than IN PLACE OF it is the whole difference between five distinguishable cards and
          // the five identical ones D58 was written to end. Nothing is invented for a card
          // that carries neither — it keeps today's untitled shape.
          const headline = a.ownerHeadline !== undefined && a.ownerHeadline !== '' ? a.ownerHeadline : null;
          const subject = a.ownerSubject !== undefined && a.ownerSubject !== '' ? a.ownerSubject : null;
          const title = headline ?? subject;
          // `tone` (informational / bookkeeping / decision / must-fix) now rides the wire too,
          // and is DELIBERATELY NOT RENDERED here. It grades how to READ a note; the only chip
          // vocabulary this room has is `severityChip`, which grades how badly something
          // blocks — a different axis. Painting one as the other would be inventing a meaning
          // the machine never wrote, and a fourth badge nobody asked for is not worth a card's
          // width. It is carried, unshown, until a surface has an honest place for it.
          return (
            // Key carries the content: RUN-006's live round held two DISTINCT advisories under one
            // id (the finding-id collision, blind-audit finding 4 — fixed at the mint, but a round
            // written by an older build can still carry the collision and must render both cards).
            <li key={key} className="qadvisory">
              {title !== null || count > 1 ? (
                <span className="qadvisory__subject">
                  {title}
                  {count > 1 && <span className="qcount">×{count}</span>}
                </span>
              ) : null}
              {headline !== null && subject !== null && (
                <span className="qadvisory__about">{subject}</span>
              )}
              {a.ownerValue !== undefined && a.ownerValue !== '' && (
                <span className="qadvisory__value">{a.ownerValue}</span>
              )}
              {stripFor?.(a)}
              <span className="qadvisory__message">{split.owner}</span>
              <EngineerDetail text={split.engineer} />
              {count > 1 && (
                <span className="qadvisory__repeats">
                  The machine settled this same thing {count} times on this build
                  {runClosed ? '.' : ' — one correction here answers all of them.'}
                </span>
              )}
              {recorded !== null ? (
                <p className="qadvisory__recorded" role="status">
                  Correction recorded{recorded !== '' ? `: “${recorded}”` : ''}
                  {runClosed
                    ? ' (kept for the record — this build is finished).'
                    : CONSUMED_STAGES.has(a.stage)
                      ? ' — this build re-derives with it when it next runs.'
                      : ' — it rides this build as a standing work item until an engineer lands it.'}
                </p>
              ) : !runClosed && correctable(a) ? (
                openIds[a.id] === true ? (
                  <div className="qadvisory__correct">
                    <textarea
                      className="form__textarea qcard__text"
                      aria-label={`Correct: ${a.id}`}
                      placeholder="Say what’s actually right here — in your own words."
                      value={drafts[a.id] ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                      disabled={busyId !== null}
                      rows={2}
                    />
                    <div className="form__actions">
                      <button
                        className="btn btn--primary btn--sm"
                        type="button"
                        onClick={() => void sendCorrection(a, ids)}
                        disabled={busyId !== null || (drafts[a.id] ?? '').trim() === ''}
                      >
                        {busyId === a.id ? 'Sending…' : 'Send correction'}
                      </button>
                      <button
                        className="btn btn--quiet btn--sm"
                        type="button"
                        onClick={() => setOpenIds((o) => ({ ...o, [a.id]: false }))}
                        disabled={busyId !== null}
                      >
                        Never mind
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn btn--quiet btn--sm"
                    type="button"
                    onClick={() => setOpenIds((o) => ({ ...o, [a.id]: true }))}
                  >
                    This is wrong — correct it
                  </button>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      {error !== null && (
        <p className="form__error" role="alert">
          {error}
        </p>
      )}
      {sentence !== null && (
        <p className="form__success" role="status">
          {sentence}
        </p>
      )}
    </details>
  );
}

/**
 * Finding 7 (answer-pipeline audit): answers whose question drifted away across resumes live in
 * `checkpoint-answers.json` forever but LOST THEIR DISPLAY — nothing joined them to a live row, so a
 * note the owner attached to an approval (a "receipt note") became invisible the moment the id
 * re-minted. Exported; rendered on the OVERVIEW and nowhere else: a banked answer carries only its
 * question's id, its text and an optional note (`CheckpointAnswerSchema`) — no stage, and no copy of
 * the question — so there is no room it could honestly be filed under. Grouping them in one place and
 * saying why is the honest shape; guessing a room from a dead id is not.
 *
 * M6: identical rows fold with an ×N count. Five of six on the measured run were word-for-word the
 * same sentence, and the record read like the machine stuttering.
 */
export function OrphanedAnswers({
  round,
  answers,
}: {
  round: RunQuestionsResult['round'];
  answers: readonly CheckpointAnswer[];
}) {
  const liveIds = new Set([...round.questions, ...(round.advisories ?? [])].map((q) => q.id));
  const orphans = answers.filter((a) => !liveIds.has(a.questionId));
  if (orphans.length === 0) return null;
  const rows = countIdentical(orphans, (a) => `${a.answer} ${a.note ?? ''}`);
  return (
    <details className="qround__advisories">
      <summary>Earlier answers kept on record ({orphans.length})</summary>
      <p className="qround__advisory-note">
        These answers belong to questions from an earlier round of this build — the question list
        moves as the build advances. They are kept on record, and decisions among them were banked
        by content, so they still count even though their original card is gone. The question each
        one answered is not kept with it, so there is nothing here to put beside them — this is the
        whole record there is.
      </p>
      <ul className="qround__advisory-list">
        {rows.map(({ item: a, count, key }) => (
          <li key={key} className="qadvisory">
            <span className="qadvisory__value">
              “{a.answer}”{count > 1 && <span className="qcount">×{count}</span>}
            </span>
            {a.note !== undefined && a.note !== '' && (
              <span className="qadvisory__message">note: {a.note}</span>
            )}
            {count > 1 && (
              <span className="qadvisory__repeats">
                The same answer was recorded {count} times, against {count} questions that have
                since drifted away.
              </span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function QuestionCard({
  q,
  value,
  onChange,
  disabled,
  noteValue,
  onNoteChange,
  stripFor,
}: {
  q: CheckpointQuestion;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  noteValue?: string;
  onNoteChange?: (v: string) => void;
  stripFor?: StripFor;
}) {
  const chip = severityChip(q);
  const split = splitEngineerDetail(q.message);
  return (
    <li className="qcard">
      <div className="qcard__head">
        <span className={`qchip ${chip.cls}`}>{chip.label}</span>
        {q.ownerSubject !== undefined && q.ownerSubject !== '' && (
          <span className="qcard__subject">{q.ownerSubject}</span>
        )}
      </div>
      {stripFor?.(q)}
      <p className="qcard__message">{split.owner}</p>
      <EngineerDetail text={split.engineer} />
      {q.ownerValue !== undefined && q.ownerValue !== '' && (
        <p className="qcard__value">{q.ownerValue}</p>
      )}
      <QuestionInput
        q={q}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...(noteValue !== undefined ? { noteValue } : {})}
        {...(onNoteChange !== undefined ? { onNoteChange } : {})}
      />
      {q.repairAction !== '' && <p className="qcard__repair">{q.repairAction}</p>}
    </li>
  );
}

/**
 * THE ANSWERING MACHINERY, for whichever slice of the round is on this screen.
 *
 * Exported because the round is no longer rendered in one place: the Overview renders the
 * questions no room can hold, and each room renders its own. The semantics are identical in
 * both — same drafts, same notes, same `runs.answer` call with the same batch shape. Only the
 * LIST differs, which is the whole of what "questions in context" changed. A partial batch is
 * already the server's normal case (it answers what it is given and reports the count), so a
 * room submitting its own questions needs nothing new from the door.
 */
export function QuestionList({
  questions,
  answers,
  onSubmit,
  stripFor,
  sendLabel = 'Send answers',
}: {
  questions: readonly CheckpointQuestion[];
  answers: readonly CheckpointAnswer[];
  onSubmit: (answers: CheckpointAnswer[]) => Promise<AnswerRunResult>;
  stripFor?: StripFor;
  sendLabel?: string;
}) {
  // Drafts, seeded from the answers already recorded (re-answering overwrites by id).
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const a of answers) seed[a.questionId] = a.answer;
    return seed;
  });
  // Per-question NOTES (B16): the words riding a HOLD answer as re-derivation guidance.
  const [notes, setNotes] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const a of answers) if (a.note !== undefined && a.note !== '') seed[a.questionId] = a.note;
    return seed;
  });
  const [busy, setBusy] = useState(false);
  const [sentence, setSentence] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (questions.length === 0) return null;

  const answerable = questions.filter((q) => q.answerKind !== 'none');
  const batch: CheckpointAnswer[] = answerable
    .filter((q) => (drafts[q.id] ?? '').trim() !== '')
    .map((q) => ({
      questionId: q.id,
      answer: drafts[q.id],
      ...((notes[q.id] ?? '').trim() !== '' ? { note: notes[q.id].trim() } : {}),
    }));

  async function onSend() {
    setBusy(true);
    setError(null);
    setSentence(null);
    try {
      const result = await onSubmit(batch);
      setSentence(answerResultSentence(result));
    } catch (err) {
      setError(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ul className="qround__list">
        {questions.map((q) => (
          <QuestionCard
            key={q.id}
            q={q}
            value={drafts[q.id] ?? ''}
            onChange={(v) => {
              setDrafts((d) => ({ ...d, [q.id]: v }));
              setSentence(null);
            }}
            disabled={busy}
            noteValue={notes[q.id] ?? ''}
            onNoteChange={(v) => setNotes((n) => ({ ...n, [q.id]: v }))}
            {...(stripFor !== undefined ? { stripFor } : {})}
          />
        ))}
      </ul>

      {error !== null && (
        <p className="form__error" role="alert">
          {error}
        </p>
      )}
      {sentence !== null && (
        <p className="form__success" role="status">
          {sentence}
        </p>
      )}

      {answerable.length > 0 && (
        <div className="form__actions">
          <button
            className="btn btn--primary"
            type="button"
            onClick={() => void onSend()}
            disabled={busy || batch.length === 0}
          >
            {busy ? 'Sending…' : sendLabel}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * THE ROUND SUMMARY — the front desk's map of an open round.
 *
 * The Overview used to host the round itself, which put a question about the rules on a page
 * with no rules on it. Now it says what was asked and where each answer belongs, and the rooms
 * hold the cards. Every fragment is a real link, and the counts are the round's own.
 */
function RoundSummary({
  runId,
  questions,
}: {
  runId: string;
  questions: readonly CheckpointQuestion[];
}) {
  const fragments = roundFragments(questions).filter((f) => f.room !== null);
  if (fragments.length === 0) return null;
  const routed = fragments.reduce((n, f) => n + f.count, 0);
  return (
    <div className="qsummary" aria-label="What the machine asked">
      <p className="qsummary__lede">
        The machine stopped to ask — {countPhrase(questions.length)}
        {routed < questions.length ? `, ${routed} of them about a part of your app` : ''}.{' '}
        {routed === 1 ? 'It is' : 'They are'} waiting in the{' '}
        {fragments.length === 1 ? 'room' : 'rooms'} {routed === 1 ? 'it is' : 'they are'} about,
        beside the thing {routed === 1 ? 'it asks' : 'they ask'} about.
      </p>
      <ul className="qsummary__rooms">
        {fragments.map((f) => (
          <li key={f.room}>
            <Link className="btn btn--ghost btn--sm" to={PATHS.runRoom(runId, f.room as string)}>
              {countPhrase(f.count)} about {f.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function QuestionRound({
  runId,
  questions,
  onSubmit,
  onResume,
  resumeBusy = false,
  stripFor,
}: {
  /** The run, for the room links in the summary. */
  runId: string;
  questions: RunQuestionsResult;
  onSubmit: (answers: CheckpointAnswer[]) => Promise<AnswerRunResult>;
  /** B14 (run006): the resume act, for the stalls whose only remedy IS a resume — an
   *  all-`answerKind:'none'` round offered ZERO acts and the owner was stuck at the last
   *  door before their app, unblocked only through the raw API. Optional: absent keeps
   *  the old render (tests, embeddings). */
  onResume?: () => void;
  resumeBusy?: boolean;
  stripFor?: StripFor;
}) {
  const { round, answers } = questions;

  const advisories = round.advisories ?? [];
  // What no room can hold renders HERE, in full — the unanswerable-nowhere guarantee.
  const here = routeQuestions(round.questions).overview;
  const hereAdvisories = routeQuestions(advisories).overview;

  const answerable = round.questions.filter((q) => q.answerKind !== 'none');
  const unanswerable = round.questions.filter((q) => q.answerKind === 'none');

  // B18: is every answerable question already RECORDED answered? (The none-kind ones are
  // unanswerable by definition — the server folds the same way since the B18 fix.) This is a
  // ROUND-level fact, so it stays on the front desk even when the cards are in rooms.
  const recordedIds = new Set(answers.map((a) => a.questionId));
  const roundComplete = answerable.length > 0 && answerable.every((q) => recordedIds.has(q.id));

  if (round.status === 'auto-approved' && round.questions.length === 0) {
    // THE B1 SHAPE (run006): auto-approved, zero questions — and possibly a list of
    // assumptions made on the owner's behalf. This return used to swallow the advisories
    // entirely (8 of them, measured). They are the ONLY content here, so they render OPEN.
    return (
      <div className="qround" aria-label="Question round">
        <p className="qround__title">
          {advisories.length > 0
            ? 'This round approved itself — but the machine settled some things for you. Read them.'
            : 'This round approved itself — nothing needed from you.'}
        </p>
        <AdvisoriesRouted runId={runId} advisories={advisories} />
        <AdvisoriesBlock
          advisories={hereAdvisories}
          answers={answers}
          onSubmit={onSubmit}
          open
          {...(stripFor !== undefined ? { stripFor } : {})}
        />
        <OrphanedAnswers round={round} answers={answers} />
      </div>
    );
  }

  return (
    <div className="qround" aria-label="Question round">
      <p className="qround__title">
        {unanswerable.length === round.questions.length && round.questions.length > 0
          ? 'The build stopped — this needs an engineer'
          : 'The machine needs you before it can continue'}
      </p>

      <RoundSummary runId={runId} questions={round.questions} />

      {here.length > 0 && (
        <>
          {here.length < round.questions.length && (
            <p className="qround__here">
              {countPhrase(here.length)} here{' '}
              {here.length === 1 ? 'belongs' : 'belong'} to no one part of your app, so{' '}
              {here.length === 1 ? 'it is' : 'they are'} asked right here.
            </p>
          )}
          <QuestionList
            questions={here}
            answers={answers}
            onSubmit={onSubmit}
            {...(stripFor !== undefined ? { stripFor } : {})}
          />
        </>
      )}

      {/* B14: an all-none round has NOTHING to answer — its remedy is a repair, then a
          resume. This screen used to offer zero acts; the owner was permanently stuck
          at the last door before their app (unblocked via the raw API — the deviation). */}
      {answerable.length === 0 && round.questions.length > 0 && onResume !== undefined && (
        <div className="qround__resume">
          <p className="qround__resume-note">
            Once a fix has landed, resume the build — everything already finished replays
            free, and if the problem still stands the machine stops here again at no cost.
          </p>
          <button
            className="btn btn--primary"
            type="button"
            onClick={onResume}
            disabled={resumeBusy}
          >
            {resumeBusy ? 'Resuming…' : 'Resume the build'}
          </button>
        </div>
      )}

      {/* B18: a fully-answered round that the machine did not auto-continue (the server
          resumes on the completing batch; this covers answers restored from disk, an
          earlier session, or an auto-resume refusal) still needs its one-click act. */}
      {roundComplete && round.status === 'awaiting-answers' && onResume !== undefined && (
        <div className="qround__resume">
          <p className="qround__resume-note">
            Every question here is answered. Continue — the machine re-derives with your
            answers.
          </p>
          <button
            className="btn btn--primary"
            type="button"
            onClick={onResume}
            disabled={resumeBusy}
          >
            {resumeBusy ? 'Continuing…' : 'Continue the build'}
          </button>
        </div>
      )}

      <AdvisoriesRouted runId={runId} advisories={advisories} />
      <AdvisoriesBlock
        advisories={hereAdvisories}
        answers={answers}
        onSubmit={onSubmit}
        {...(stripFor !== undefined ? { stripFor } : {})}
      />
      <OrphanedAnswers round={round} answers={answers} />
    </div>
  );
}

/**
 * WHERE THE ADVISORIES WENT — the Overview's pointer to them.
 *
 * "Settled for you" is a per-stage record: each advisory carries the stage whose derivation
 * settled it, so it belongs in that stage's room beside the artifact it settled. What is left
 * on the front desk is the count and the way there.
 */
export function AdvisoriesRouted({
  runId,
  advisories,
}: {
  runId: string;
  advisories: readonly CheckpointQuestion[];
}) {
  const fragments = roundFragments(advisories).filter((f) => f.room !== null);
  if (fragments.length === 0) return null;
  const routed = fragments.reduce((n, f) => n + f.count, 0);
  return (
    <div className="qsummary qsummary--advisories" aria-label="Settled for you">
      <p className="qsummary__lede">
        The machine settled {countPhrase(routed, 'thing')} on your behalf while it worked. Each one
        is on record in the room it belongs to, with what it assumed and — while the build can
        still run — the way to correct it.
      </p>
      <ul className="qsummary__rooms">
        {fragments.map((f) => (
          <li key={f.room}>
            <Link className="btn btn--ghost btn--sm" to={PATHS.runRoom(runId, f.room as string)}>
              {f.count} settled in {f.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
